import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  DialogActions,
  Typography,
  Modal as MuiModal,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Checkbox,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PropTypes from 'prop-types';
import { useApiClient } from '../../context/ApiClientContext';
import { axiosErrorToUserMessage } from '../../services/axios';
import { Controls } from '../../components/controls/Controls';
import useSitePermissionUpdate from '../../hooks/useSitePermissionUpdate';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: '8px',
  p: 4,
};

function UserAssignedSitesModalButton(props) {
  const {
    user,
    fetchUsers,
    isOpen,
    onOpen,
    onClose,
    totalSiteCount,
    setSuccessMessage,
    setErrorMessage,
    allSites,
  } = props;

  const [selectedSites, setSelectedSites] = useState([]);
  const apiClient = useApiClient();
  const { handleSiteUnassignment, getCurrentUsername } = useSitePermissionUpdate();

  let siteCountText;
  if (user.siteIds.includes('*')) {
    siteCountText = `${totalSiteCount} Sites`;
  } else {
    siteCountText = `${user.siteIds.length} Sites`;
  }

  const sitesList = useMemo(() => {
    if (!isOpen) return [];

    let userSites = [];
    if (user.siteIds.includes('*')) {
      userSites = allSites;
    } else {
      const sitesMap = new Map(allSites.map((site) => [site.id, site]));

      userSites = user.siteIds.map((siteId) => {
        const foundSite = sitesMap.get(siteId);
        if (foundSite) {
          return foundSite;
        }
        return {
          id: siteId,
          name: `Could not find site with id ${siteId}.`,
        };
      });
    }
    return userSites;
  }, [isOpen, allSites, user.siteIds]);

  // Initialize selected sites when modal opens and sites data is available
  useEffect(() => {
    if (isOpen && sitesList.length > 0) {
      setSelectedSites([]); // Start with no selected sites
    }
  }, [isOpen, sitesList]);

  const handleSiteCheckboxChange = (siteId) => {
    setSelectedSites((prevSelectedSites) => {
      if (prevSelectedSites.includes(siteId)) {
        return prevSelectedSites.filter((id) => id !== siteId);
      }
      return [...prevSelectedSites, siteId];
    });
  };

  const handleUnassignSites = async () => {
    const sitesToUnassign = sitesList
      .filter((site) => selectedSites.includes(site.id))
      .map((site) => site.id);

    if (sitesToUnassign.length > 0) {
      try {
        const response = await apiClient.unassignSitesFromUser(user.username, sitesToUnassign);
        if (response.status === 200) {
          // If current user is unassigning sites from themselves,
          // remove sites from local accessible list and force token refresh to sync with server
          const currentUsername = getCurrentUsername();
          if (currentUsername && currentUsername === user.username) {
            await handleSiteUnassignment(sitesToUnassign);
          }
          setSuccessMessage(response.data.message);
          await fetchUsers();
          onClose();
        }
      } catch (error) {
        setErrorMessage(
          axiosErrorToUserMessage(error) || 'Something went wrong while unassigning sites.',
        );
      }
    } else {
      setErrorMessage('Please select at least one site to unassign.');
    }
  };

  const selectableIds = user.siteIds.includes('*') ? [] : sitesList.map((site) => site.id);
  const isAllSelected = selectableIds.length && selectedSites.length === selectableIds.length;
  const isIndeterminate = selectedSites.length && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedSites([]);
    } else {
      setSelectedSites(sitesList.map((site) => site.id));
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  let modalBodyContent;

  if (sitesList.length === 0) {
    modalBodyContent = (
      <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
        This user has no sites assigned.
      </Typography>
    );
  } else {
    modalBodyContent = (
      <List>
        <ListItemButton
          onClick={handleSelectAll}
          dense
          disabled={user.siteIds.includes('*')}
        >
          <ListItemIcon sx={{ minWidth: '40px', display: 'flex', alignItems: 'center' }}>
            <Checkbox
              edge="start"
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              disabled={user.siteIds.includes('*')}
            />
          </ListItemIcon>
          <ListItemText primary="Select All for Removal" />
        </ListItemButton>
        {sitesList.map((site) => (
          <ListItemButton
            key={site.id}
            onClick={() => handleSiteCheckboxChange(site.id)}
            dense
            disabled={user.siteIds.includes('*')}
          >
            <ListItemIcon sx={{ minWidth: '40px', display: 'flex', alignItems: 'center' }}>
              {selectedSites.includes(site.id) ? (
                <RemoveCircleOutlineIcon color="primary" sx={{ fontSize: '20px' }} />
              ) : (
                <RadioButtonUncheckedIcon color="action" sx={{ fontSize: '20px' }} />
              )}
            </ListItemIcon>
            <ListItemText id={site.id} primary={site.name} />
          </ListItemButton>
        ))}
      </List>
    );
  }

  return (
    <>
      <Controls.Button
        variant="outlined"
        size="small"
        onClick={onOpen}
        text={siteCountText}
      />
      <MuiModal
        open={isOpen}
        onClose={handleCloseModal}
        aria-labelledby="view-sites-modal"
        aria-describedby="view-sites-modal"
        data-testid="view-sites-modal"
      >
        <Box sx={modalStyle}>
          <Typography
            id="view-sites-modal"
            variant="h6"
            component="h2"
            sx={{ marginBottom: '16px' }}
          >
            {user.username}
            &apos;s Sites
          </Typography>
          {user.siteIds.includes('*') && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                marginBottom: '16px',
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
              }}
            >
              This user has access to all sites. Sites cannot be unassigned
              when user has full site list.
            </Typography>
          )}
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '10px',
              height: '500px',
              overflow: 'auto',
            }}
          >
            {modalBodyContent}
          </Box>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              onClick={handleUnassignSites}
              variant="contained"
              color="primary"
              disabled={user.siteIds.includes('*') || sitesList.length === 0}
            >
              Unassign
            </Button>
          </DialogActions>
        </Box>
      </MuiModal>
    </>
  );
}

UserAssignedSitesModalButton.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string.isRequired,
    siteIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  fetchUsers: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  totalSiteCount: PropTypes.number.isRequired,
  allSites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  setSuccessMessage: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
};

UserAssignedSitesModalButton.defaultProps = {
  allSites: [],
};

export default UserAssignedSitesModalButton;
